// src/utils/graphLayout.js
import { buildFlowNode } from './flowNodeData';
import { buildNavigationEdges } from './flowEdgeUtils';

const LAYER_SPACING = 360;
const NODE_HEIGHT_COMPACT = 168;
const NODE_HEIGHT_DETAILED = 216;

export function createInitialGraphDFS(workflowData, showDetailedContent = false) {
  const nodeMap = {};
  const adjacency = {};
  if (!workflowData || workflowData.length === 0) {
    return { layoutNodes: [], layoutEdges: [] };
  }
  workflowData.forEach((view) => {
    nodeMap[view.id] = view;
    adjacency[view.id] = [];
  });

  workflowData.forEach((view) => {
    (view.actions || []).forEach((action) => {
      if (action.type === 'navigation') {
        adjacency[view.id].push(action.link);
      }
    });
  });

  const visited = new Set();
  const positions = {};
  let nextY = 0;
  const layerSpacing = LAYER_SPACING;
  const nodeHeight = showDetailedContent ? NODE_HEIGHT_DETAILED : NODE_HEIGHT_COMPACT;

  function dfs(nodeId, depth) {
    visited.add(nodeId);

    const children = adjacency[nodeId] || [];
    let hasChildren = false;
    let subtreeTop = Infinity;
    let subtreeBottom = -Infinity;

    for (const childId of children) {
      if (!visited.has(childId)) {
        hasChildren = true;
        const { top, bottom } = dfs(childId, depth + 1);
        if (top < subtreeTop) subtreeTop = top;
        if (bottom > subtreeBottom) subtreeBottom = bottom;
      }
    }

    if (!hasChildren) {
      positions[nodeId] = { x: depth * layerSpacing, y: nextY };
      subtreeTop = nextY;
      subtreeBottom = nextY;
      nextY += nodeHeight;
    } else {
      const centerY = (subtreeTop + subtreeBottom) / 2;
      positions[nodeId] = { x: depth * layerSpacing, y: centerY };
    }
    return { top: subtreeTop, bottom: subtreeBottom };
  }

  // Start from "start" if it exists
  if (nodeMap['start']) {
    dfs('start', 0);
  }

  // Ensure disconnected/unreachable nodes are still rendered.
  // We stack them in a separate "unconnected" column so they are visible.
  const maxConnectedX = Object.values(positions).reduce(
    (maxX, pos) => Math.max(maxX, pos?.x ?? 0),
    0
  );
  const disconnectedX = maxConnectedX + layerSpacing * 2;

  workflowData.forEach((view) => {
    if (!visited.has(view.id)) {
      positions[view.id] = { x: disconnectedX, y: nextY };
      nextY += nodeHeight;
    }
  });

  // Build layoutNodes
  const layoutNodes = Object.keys(positions).map((nodeId) => {
    const view = nodeMap[nodeId];
    const step = view || { id: nodeId, heading: nodeId, text: '', type: 'html', actions: [] };
    return buildFlowNode(step, positions[nodeId], showDetailedContent);
  });

  const layoutEdges = buildNavigationEdges(workflowData);

  return { layoutNodes, layoutEdges };
}